import { computed, ref, watch } from "vue";
import { createTimezoneOptions, type TimezoneOption } from "@lunagoai/timezone";
import {
  createJsonStorageAdapter,
  createLanguageController,
  createSettingsStore,
  createThemeController,
  provideAppCore,
  type ThemeController,
} from "@lunagoai/vue-app-core";
import type { Locale } from "./i18n";
import { getBrowserTimeZone, isValidTimeZone, setDisplayTimeZone } from "./app/presentation";

export type AppSettings = { theme: "light" | "dark" | "system"; language: Locale; timeZone: string };

const availableLocales: Locale[] = ["zh-TW", "en-US"];
const availableThemes: AppSettings["theme"][] = ["light", "dark", "system"];

export const settings = createSettingsStore<AppSettings>({
  defaults: { theme: "system", language: "zh-TW", timeZone: getBrowserTimeZone() },
  storage: createJsonStorageAdapter<AppSettings>({ key: "gcs-clipping-settings" }),
  migrate(stored, defaults) {
    if (!stored || typeof stored !== "object") return defaults;
    const candidate = stored as Partial<AppSettings>;
    return {
      theme: availableThemes.includes(candidate.theme as AppSettings["theme"]) ? candidate.theme as AppSettings["theme"] : defaults.theme,
      language: availableLocales.includes(candidate.language as Locale) ? candidate.language as Locale : defaults.language,
      timeZone: typeof candidate.timeZone === "string" && isValidTimeZone(candidate.timeZone) ? candidate.timeZone : defaults.timeZone,
    };
  },
});

const languageRef = ref<Locale>(settings.settings.value.language);
const themeRef = ref<AppSettings["theme"]>(settings.settings.value.theme);
export const timeZone = ref<string>(settings.settings.value.timeZone);
const timeZoneRef = timeZone;
setDisplayTimeZone(timeZoneRef.value);

export const language = createLanguageController({
  currentLanguage: languageRef as any,
  availableLanguages: availableLocales,
  setLanguage: (locale) => { languageRef.value = locale as Locale; },
});

export function createGcsTimezoneOptions(locale: Locale = languageRef.value, additionalTimezones: readonly string[] = []): TimezoneOption[] {
  return createTimezoneOptions({
    locale: locale === "zh-TW" ? "zh-Hant" : "en",
    additionalTimezones: [timeZoneRef.value, ...additionalTimezones],
  });
}

const baseTheme = createThemeController({ theme: themeRef as any });
const systemMedia = typeof window === "undefined" ? undefined : window.matchMedia("(prefers-color-scheme: dark)");
const systemDark = ref(Boolean(systemMedia?.matches));
const isDark = computed(() => themeRef.value === "dark" || (themeRef.value === "system" && systemDark.value));

function applyResolvedTheme(): void {
  document.documentElement.classList.toggle("dark", isDark.value);
  document.documentElement.style.colorScheme = isDark.value ? "dark" : "light";
}

function handleSystemThemeChange(event: MediaQueryListEvent): void {
  systemDark.value = event.matches;
  if (themeRef.value === "system") applyResolvedTheme();
}

systemMedia?.addEventListener("change", handleSystemThemeChange);

export const theme = {
  theme: themeRef,
  isDark,
  setTheme(mode: AppSettings["theme"]) {
    themeRef.value = mode;
    baseTheme.setTheme(mode);
    applyResolvedTheme();
  },
  apply() {
    baseTheme.apply();
    applyResolvedTheme();
  },
  stop() {
    baseTheme.stop();
    systemMedia?.removeEventListener("change", handleSystemThemeChange);
  },
} as unknown as ThemeController<AppSettings["theme"]>;
theme.apply();

watch(languageRef, (locale) => settings.set("language", locale));
watch(themeRef, (mode) => {
  settings.set("theme", mode);
  theme.apply();
});
watch(timeZoneRef, (timeZone) => {
  settings.set("timeZone", timeZone);
  setDisplayTimeZone(timeZone);
});

export function provideGcsAppCore() {
  provideAppCore({ settings, language, theme });
  return { settings, language, theme, timeZone: timeZoneRef };
}
